/**
 * Gestionnaire d'annulation/restauration pour l'éditeur riche
 */
export class UndoRedoManager {
    constructor(editor) {
        this.editor = editor;
        this.editorContent = editor.editorContent;
        this.history = [];
        this.currentIndex = -1;
        this.maxHistorySize = 50;
        this.debounceTimeout = null;
        this.lastSaveTime = 0;
        this.minSaveInterval = 500; // 500ms minimum entre les sauvegardes
        
        this.setupEventListeners();
        this.saveState(); // État initial
    }
    
    setupEventListeners() {
        // Sauvegarder l'état lors des modifications
        this.editorContent.addEventListener('input', () => {
            this.debouncedSaveState();
        });
        
        // Raccourcis clavier pour annuler/restaurer
        this.editorContent.addEventListener('keydown', event => {
            if (event.ctrlKey || event.metaKey) {
                if (event.key === 'z' && !event.shiftKey) {
                    event.preventDefault();
                    this.undo();
                } else if (event.key === 'z' && event.shiftKey || event.key === 'y') {
                    event.preventDefault();
                    this.redo();
                }
            }
        });
    }
    
    debouncedSaveState() {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(() => {
            const now = Date.now();
            if (now - this.lastSaveTime >= this.minSaveInterval) {
                this.saveState();
                this.lastSaveTime = now;
            }
        }, 300);
    }
    
    saveState() {
        const currentState = {
            content: this.editorContent.innerHTML,
            selection: this.saveSelection(),
            timestamp: Date.now()
        };
        
        // Ne pas sauvegarder si le contenu n'a pas changé
        if (this.history.length > 0 && 
            this.history[this.currentIndex] && 
            this.history[this.currentIndex].content === currentState.content) {
            return;
        }
        
        // Supprimer les états futurs si on est au milieu de l'historique
        if (this.currentIndex < this.history.length - 1) {
            this.history.splice(this.currentIndex + 1);
        }
        
        // Ajouter le nouvel état
        this.history.push(currentState);
        this.currentIndex = this.history.length - 1;
        
        // Limiter la taille de l'historique
        if (this.history.length > this.maxHistorySize) {
            this.history.shift();
            this.currentIndex--;
        }
        
        this.updateButtons();
    }
    
    undo() {
        if (this.canUndo()) {
            this.currentIndex--;
            this.restoreState(this.history[this.currentIndex]);
            this.updateButtons();
        }
    }
    
    redo() {
        if (this.canRedo()) {
            this.currentIndex++;
            this.restoreState(this.history[this.currentIndex]);
            this.updateButtons();
        }
    }
    
    canUndo() {
        return this.currentIndex > 0;
    }
    
    canRedo() {
        return this.currentIndex < this.history.length - 1;
    }
    
    restoreState(state) {
        this.editorContent.innerHTML = state.content;
        
        if (state.selection) {
            setTimeout(() => {
                this.restoreSelection(state.selection);
            }, 0);
        }
    }
    
    saveSelection() {
        const selection = window.getSelection();
        if (!selection.rangeCount) return null;
        
        const range = selection.getRangeAt(0);
        return {
            startContainer: this.getNodePath(range.startContainer),
            startOffset: range.startOffset,
            endContainer: this.getNodePath(range.endContainer),
            endOffset: range.endOffset,
            collapsed: range.collapsed
        };
    }
    
    restoreSelection(savedSelection) {
        if (!savedSelection) return;
        
        try {
            const startContainer = this.getNodeByPath(savedSelection.startContainer);
            const endContainer = this.getNodeByPath(savedSelection.endContainer);
            
            if (startContainer && endContainer) {
                const range = document.createRange();
                range.setStart(startContainer, Math.min(savedSelection.startOffset, startContainer.length || 0));
                range.setEnd(endContainer, Math.min(savedSelection.endOffset, endContainer.length || 0));
                
                const selection = window.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
            }
        } catch (error) {
            console.warn('Could not restore selection:', error);
        }
    }
    
    getNodePath(node) {
        const path = [];
        let current = node;
        
        while (current && current !== this.editorContent) {
            const parent = current.parentNode;
            if (parent) {
                const index = Array.from(parent.childNodes).indexOf(current);
                path.unshift(index);
            }
            current = parent;
        }
        
        return path;
    }
    
    getNodeByPath(path) {
        let current = this.editorContent;
        
        for (const index of path) {
            if (current.childNodes[index]) {
                current = current.childNodes[index];
            } else {
                return null;
            }
        }
        
        return current;
    }
    
    updateButtons() {
        // Mettre à jour l'état des boutons undo/redo si ils existent
        const undoButton = this.editor.container.querySelector('button.ui-button.undo');
        const redoButton = this.editor.container.querySelector('button.ui-button.redo');
        
        if (undoButton) {
            undoButton.disabled = !this.canUndo();
        }
        
        if (redoButton) {
            redoButton.disabled = !this.canRedo();
        }
    }
    
    clear() {
        this.history = [];
        this.currentIndex = -1;
        this.saveState();
    }
}